import {
  arrayOf,
  bool,
  func,
  number,
  oneOfType,
  shape,
  string,
} from "prop-types";
import React, { Fragment, useEffect } from "react";

import styled from "styled-components/macro";

import classNames from "classnames";
import { Reorder } from "framer-motion";

import FnrStaggeredEntry from "UI/FnrStaggeredEntry";

import clearSelection from "Utilities/clearSelection";

import ListItem from "./ListItem";

export default function FnrDragNDrop({
  items,
  itemIdName,
  onReorder,
  ItemComponent,
  onRelease,
  disabled,
  itemHasDragTrigger, // true if ItemComponent includes an element that is required to be clicked to start dragging vs the whole item can be clicked to drag it
  noMargin,
  Divider,
  staggered,
  ...props
}) {
  const [{ orderedItems, id, startPosition, endPosition, isActive }, dispatch] =
    React.useReducer(orderReducer, initialOrderState);

  useEffect(() => {
    dispatch({ type: "setOrderedItems", payload: items });
  }, [items]);

  function handleReorder(newItems) {
    // set the reordered items

    // sets the current position of the item being dragged in orderState
    let currentPosition = newItems.findIndex(
      (status) => status[itemIdName] === id
    );

    dispatch({
      type: "handleReorder",
      payload: {
        orderedItems: newItems,
        endPosition: currentPosition,
      },
    });

    if (onReorder) {
      onReorder(newItems);
    }
  }

  function handleStartReordering(id) {
    dispatch({
      type: "startReordering",
      payload: {
        id,
        startPosition: items.findIndex((status) => status[itemIdName] === id),
        isActive: true,
      },
    });
  }

  function handleRelease() {
    // console.log("handlingRelease", { isActive, endPosition });
    const hasEndPosition = endPosition !== null && endPosition !== undefined;
    if (isActive && hasEndPosition && onRelease) {
      // console.log("hits in fnrdragndrop");
      // Pass all the ordered data so that parent component can do whatever it wants with it
      onRelease({
        id,
        startPosition,
        endPosition,
        orderedItems,
      });
    }

    dispatch({ type: "reset" });
    clearSelection();
  }

  if (!orderedItems) return null;

  return (
    <ReorderGroup
      axis="y"
      values={orderedItems}
      onReorder={handleReorder}
      disabled={disabled}
      $noMargin={noMargin}
      className={classNames({ isDragging: isActive })}
    >
      <FnrStaggeredEntry show muted={!staggered} initOnly>
        {orderedItems?.map((item, index, allItems) => {
          const notLast = index < allItems.length - 1;
          return (
            <Fragment key={item[itemIdName]}>
              <ListItem
                key={item[itemIdName]}
                item={item}
                itemIdName={itemIdName}
                ItemComponent={ItemComponent}
                onRelease={handleRelease}
                onMouseUp={handleRelease}
                onMouseDown={handleStartReordering}
                isActiveItem={isActive && id === item[itemIdName]}
                itemHasDragTrigger={itemHasDragTrigger}
                disabled={disabled}
                index={index}
                allItems={allItems}
                {...props}
              />
              {notLast && Boolean(Divider) && <Divider />}
            </Fragment>
          );
        })}
      </FnrStaggeredEntry>
    </ReorderGroup>
  );
}

const ReorderGroup = styled(Reorder.Group)`
  list-style: none;
  margin-block: 0;
  padding: 0;
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  &.isDragging {
    user-select: none;
  }

  > * + * {
    ${({ $noMargin }) => ($noMargin ? "" : "margin-top: 5px;")}
  }
`;

const initialOrderState = {
  orderedItems: null,
  id: null,
  startPosition: null,
  endPosition: null,
  isActive: false,
};

function orderReducer(state, action) {
  switch (action.type) {
    case "setOrderedItems":
      return {
        ...state,
        orderedItems: action.payload,
      };
    case "setId":
      return {
        ...state,
        id: action.payload,
      };
    case "setStartPosition":
      return {
        ...state,
        startPosition: action.payload,
      };
    case "setEndPosition":
      return {
        ...state,
        endPosition: action.payload,
      };
    case "setIsActive":
      return {
        ...state,
        isActive: action.payload,
      };
    case "startReordering":
      return {
        ...state,
        id: action.payload.id,
        startPosition: action.payload.startPosition,
        isActive: action.payload.isActive,
      };
    case "handleReorder":
      return {
        ...state,
        orderedItems: action.payload.orderedItems,
        endPosition: action.payload.endPosition,
      };
    case "reset":
      return {
        ...state,
        id: null,
        startPosition: null,
        endPosition: null,
        isActive: false,
      };
    default:
      return state;
  }
}

FnrDragNDrop.propTypes = {
  items: arrayOf(
    shape({
      id: oneOfType([string, number]), // this doesn't have to be "id". It can be userID, householdId, etc. itemIDName is used to specify this internally
      /** props to be passed to each item */
    })
  ),
  itemIdName: string, // name of the property that contains the item's id. Ex: if the item is {userId: 1, name: 'John'}, then itemIdName would be 'userId'
  onReorder: func, // function that is called as the items are reordered. This will be in addition to the reordering that happens internally
  onRelease: func, // function that is called when the user releases the item. This is where you would trigger saving the new order to the db.
  ItemComponent: func, // component that is used to render each item
  Divider: func,
  disabled: bool,
  itemHasDragTrigger: bool,
  noMargin: bool,
  staggered: bool,
};
